import { motion } from 'framer-motion';
import { HarmonicScore } from '../types/harmonic';
import { harmonicLevels } from '../data/harmonicLevels';

// Color mapping for each harmonic level grouped by emotional band
function getHarmonicColor(levelId: number): string {
  if (levelId <= 3) return 'bg-gray-40';
  if (levelId <= 5) return 'bg-orange-40';
  if (levelId <= 7) return 'bg-brand-40';
  if (levelId <= 9) return 'bg-teal-50';
  return 'bg-success-40';
}

interface Props {
  scores: HarmonicScore[];
  compact?: boolean;
}

export function HarmonicGraph({ scores, compact = false }: Props) {
  const orderedScores = [...harmonicLevels]
    .reverse()
    .map(level => {
      return scores.find(score => score.levelId === level.id)!;
    });

  return (
    <div className="space-y-3">
      {orderedScores.map((score, index) => {
        const level = harmonicLevels.find(l => l.id === score.levelId)!;
        return (
          <motion.div
            key={level.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="relative group"
          >
            <div className="flex items-center gap-4">
              <div className={`${compact ? 'w-24' : 'w-32'} text-right flex-shrink-0`}>
                <div className={`font-medium text-gray-900 ${compact ? 'text-sm' : 'text-base'}`}>
                  {level.name}
                </div>
                {!compact && (
                  <div className="text-xs text-gray-500">
                    Level {level.id}
                  </div>
                )}
              </div>
              
              <div className={`flex-1 ${compact ? 'h-3' : 'h-4'} bg-gray-200 rounded-full overflow-hidden`}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${score.normalized}%` }}
                  transition={{ duration: 1, delay: index * 0.1 }}
                  className={`h-full rounded-full ${getHarmonicColor(level.id)}`}
                />
              </div>
              
              <div className={`${compact ? 'w-10 text-sm' : 'w-12 text-base'} font-semibold text-gray-900 text-right flex-shrink-0`}>
                {Math.round(score.normalized)}
              </div>
            </div>
            
            {!compact && (
              <div className="opacity-0 group-hover:opacity-100 absolute left-1/2 -translate-x-1/2 top-full mt-2 
                            bg-white border border-gray-200 rounded-lg p-3 text-gray-700 text-sm transition-all duration-300 z-10 w-64 shadow-lg">
                {level.description}
              </div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}